/**
 * RulesScreen — admin editor for pricing rules (hard exceptions / exception
 * approvals / leverage reductions), per program (RTL / GUC).
 *
 * Ported from digital_sizer/pages/AdminRules.tsx into eastviewos: same
 * /admin/rules read/write + audit-log history, recreated in MUI (master list
 * left, editor right) with react-query swapped for plain state via
 * adminPricingApi. Program + rule-type are in-page toggles, not route params.
 * The natural-language "assistant" RuleEditorModal is intentionally omitted
 * (it needs the copilot backend); this is the direct structured editor.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import AddRounded from "@mui/icons-material/AddRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import RuleFolderOutlined from "@mui/icons-material/RuleFolderOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import ChangeHistoryDrawer from "../../components/admin/ChangeHistoryDrawer";
import ProgramToggle from "../GridsScreen/ProgramToggle";
import PredicateBuilder, { isSimpleLeaf } from "./PredicateBuilder";
import { adminPricingApi } from "../../api/adminPricingApi";
import { teal } from "../../theme/colors";

const PRED_MODES = [
  { value: "builder", label: "Builder" },
  { value: "json", label: "JSON" },
];

const PROGRAMS = ["RTL", "GUC"];
const RULE_TYPES = [
  { value: "hard_exception_rules", label: "Hard Exceptions" },
  { value: "exception_approval_rules", label: "Exception Approvals" },
  { value: "leverage_reduction_rules", label: "Leverage Reductions" },
];
const RULE_TYPE_TO_ENTITY = {
  hard_exception_rules: "hard_exception_rule",
  exception_approval_rules: "exception_approval_rule",
  leverage_reduction_rules: "leverage_reduction_rule",
};

const stripIds = (list) =>
  // eslint-disable-next-line no-unused-vars
  list.map(({ id, _id, ...rest }) => rest);

export default function RulesScreen() {
  const [program, setProgram] = useState("RTL");
  const [type, setType] = useState("hard_exception_rules");
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedIdx, setSelectedIdx] = useState(null);
  const [draft, setDraft] = useState(null);
  const [predicateText, setPredicateText] = useState("");
  const [predicateError, setPredicateError] = useState(null);
  const [commitMsg, setCommitMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [predMode, setPredMode] = useState("builder");

  const isLeverage = type === "leverage_reduction_rules";

  const load = useCallback((t, p) => {
    setLoading(true);
    return adminPricingApi
      .getRules(t, p)
      .then((data) => setRules(Array.isArray(data) ? data : []))
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setSelectedIdx(null);
    setDraft(null);
    load(type, program);
  }, [type, program, load]);

  // Auto-select the first rule once data lands.
  useEffect(() => {
    if (!rules || rules.length === 0 || selectedIdx != null) return;
    setSelectedIdx(0);
  }, [rules, selectedIdx]);

  // Hydrate draft from the selected rule.
  useEffect(() => {
    if (selectedIdx == null || !rules[selectedIdx]) {
      setDraft(null);
      setPredicateText("");
      setPredicateError(null);
      return;
    }
    const r = rules[selectedIdx];
    setDraft({ ...r });
    setPredicateText(JSON.stringify(r.predicate ?? {}, null, 2));
    setPredicateError(null);
    // Use the friendly builder for simple leaf predicates; fall back to raw JSON
    // for anything nested / non-leaf the builder can't represent.
    setPredMode(isSimpleLeaf(r.predicate ?? {}) ? "builder" : "json");
  }, [selectedIdx, rules]);

  const persist = useCallback(
    (nextRules, commitMessage) =>
      adminPricingApi
        .saveRules(type, program, stripIds(nextRules), commitMessage)
        .then(() => load(type, program)),
    [type, program, load],
  );

  const onSave = useCallback(() => {
    if (selectedIdx == null || !draft) return;
    let predicate;
    if (predMode === "json") {
      try {
        predicate = JSON.parse(predicateText);
      } catch {
        setPredicateError("Predicate is not valid JSON");
        return;
      }
    } else {
      predicate = draft.predicate ?? {};
    }
    setBusy(true);
    const next = rules.map((r, i) => (i === selectedIdx ? { ...draft, predicate } : r));
    persist(next, commitMsg)
      .then(() => setCommitMsg(""))
      .catch((e) =>
        window.alert(`Save failed: ${e?.response?.data?.detail || e?.message || e}`),
      )
      .finally(() => setBusy(false));
  }, [selectedIdx, draft, predMode, predicateText, rules, persist, commitMsg]);

  const onDelete = useCallback(() => {
    if (selectedIdx == null) return;
    if (!window.confirm("Delete this rule?")) return;
    setBusy(true);
    const next = rules.filter((_, i) => i !== selectedIdx);
    persist(next, commitMsg || "delete rule")
      .then(() => setSelectedIdx(null))
      .catch((e) =>
        window.alert(`Delete failed: ${e?.response?.data?.detail || e?.message || e}`),
      )
      .finally(() => setBusy(false));
  }, [selectedIdx, rules, persist, commitMsg]);

  const onAdd = useCallback(() => {
    setBusy(true);
    const newRule = {
      program,
      name: "new_rule",
      message: "",
      predicate: { field: "x", op: "==", value: 0 },
      ...(isLeverage ? { reduction_pct: 0 } : {}),
    };
    const next = [...rules, newRule];
    persist(next, commitMsg || "add rule")
      .then(() => setSelectedIdx(next.length - 1))
      .catch((e) =>
        window.alert(`Add failed: ${e?.response?.data?.detail || e?.message || e}`),
      )
      .finally(() => setBusy(false));
  }, [program, isLeverage, rules, persist, commitMsg]);

  const typeLabel = useMemo(
    () => RULE_TYPES.find((r) => r.value === type)?.label,
    [type],
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "380px minmax(0, 1fr)" },
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Master list */}
      <Flx
        column
        sx={{
          height: "100%",
          minHeight: 0,
          borderRight: "1px solid #e5e7eb",
          bgcolor: "#fcfcfd",
        }}
      >
        <Box sx={{ px: 1.75, pt: 2, pb: 1.5, borderBottom: "1px solid #d8dee4" }}>
          <Flx ac g={1} sx={{ mb: 1.25 }}>
            <RuleFolderOutlined sx={{ fontSize: 18, color: teal[700] }} />
            <Txt sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Rules</Txt>
          </Flx>
          <Flx column g={1}>
            <ProgramToggle value={program} options={PROGRAMS} onChange={setProgram} />
            <ProgramToggle value={type} options={RULE_TYPES} onChange={setType} />
          </Flx>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.25, py: 1 }}>
          {loading ? (
            <Flx center sx={{ py: 5 }}>
              <CircularProgress size={20} sx={{ color: teal[700] }} />
            </Flx>
          ) : rules.length === 0 ? (
            <Flx column center sx={{ py: 5, px: 2, textAlign: "center", color: "#94a3b8" }}>
              <RuleFolderOutlined sx={{ fontSize: 30, mb: 1 }} />
              <Txt sx={{ fontSize: 12.5, mb: 1.5 }}>
                No {typeLabel?.toLowerCase()} for {program} yet.
              </Txt>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRounded sx={{ fontSize: 16 }} />}
                onClick={onAdd}
                disabled={busy}
                sx={{ textTransform: "none" }}
              >
                New rule
              </Button>
            </Flx>
          ) : (
            <>
              {rules.map((r, i) => {
                const sel = i === selectedIdx;
                return (
                  <Box
                    key={i}
                    component="button"
                    type="button"
                    onClick={() => setSelectedIdx(i)}
                    sx={{
                      appearance: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      width: "100%",
                      border: "1px solid",
                      borderColor: sel ? teal[700] : "transparent",
                      bgcolor: sel ? "#f1f6f9" : "transparent",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.85,
                      mb: 0.25,
                      "&:hover": { bgcolor: sel ? "#e4eef3" : "#e8eef2" },
                    }}
                  >
                    <Txt sx={{ fontSize: 13, fontWeight: sel ? 700 : 600, color: "#0f172a" }}>
                      {r.name}
                    </Txt>
                    {r.message && (
                      <Txt
                        sx={{
                          fontSize: 11.5,
                          color: "#64748b",
                          mt: 0.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.message}
                      </Txt>
                    )}
                  </Box>
                );
              })}
              <Button
                size="small"
                variant="text"
                startIcon={<AddRounded sx={{ fontSize: 16 }} />}
                onClick={onAdd}
                disabled={busy}
                sx={{ mt: 0.5, textTransform: "none" }}
              >
                Add rule
              </Button>
            </>
          )}
        </Box>
      </Flx>

      {/* Editor */}
      <Box sx={{ minHeight: 0, overflowY: "auto", p: { xs: 2, md: 3 } }}>
        {!draft ? (
          <Flx column center sx={{ height: "100%", color: "#94a3b8", textAlign: "center", px: 3 }}>
            <RuleFolderOutlined sx={{ fontSize: 40 }} />
            <Txt sx={{ fontSize: 16, fontWeight: 600, color: "#334155", mt: 1.5 }}>
              Select a rule
            </Txt>
            <Txt sx={{ fontSize: 13, mt: 0.5, maxWidth: 360 }}>
              Choose a rule on the left to edit, or add a new one.
            </Txt>
          </Flx>
        ) : (
          <Box sx={{ maxWidth: 720 }}>
            <Flx ac jb g={1} sx={{ mb: 2 }}>
              <Txt sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>Edit rule</Txt>
              <Button
                size="small"
                variant="outlined"
                startIcon={<HistoryRounded sx={{ fontSize: 16 }} />}
                onClick={() => setHistoryOpen(true)}
                sx={{ textTransform: "none" }}
              >
                History
              </Button>
            </Flx>

            <Flx column g={2}>
              <TextField
                size="small"
                fullWidth
                label="Name"
                value={draft.name || ""}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                fullWidth
                label="Message"
                value={draft.message || ""}
                onChange={(e) => setDraft({ ...draft, message: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              {isLeverage && (
                <TextField
                  size="small"
                  type="number"
                  label="Reduction (decimal, e.g. 0.05 = 5%)"
                  value={draft.reduction_pct ?? 0}
                  onChange={(e) =>
                    setDraft({ ...draft, reduction_pct: Number(e.target.value) })
                  }
                  inputProps={{ step: 0.01 }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ maxWidth: 280 }}
                />
              )}
              <Box>
                <Flx ac jb g={1} sx={{ mb: 1 }}>
                  <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                    Predicate — the condition that triggers this rule
                  </Txt>
                  <ProgramToggle
                    value={predMode}
                    options={PRED_MODES}
                    onChange={(m) => {
                      if (m === "json") {
                        setPredicateText(
                          JSON.stringify(draft.predicate ?? {}, null, 2),
                        );
                        setPredicateError(null);
                      }
                      setPredMode(m);
                    }}
                  />
                </Flx>

                {predMode === "builder" ? (
                  <>
                    <PredicateBuilder
                      value={draft.predicate ?? {}}
                      onChange={(p) => setDraft({ ...draft, predicate: p })}
                    />
                    <Txt
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "#94a3b8",
                        mt: 1.5,
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      Result
                    </Txt>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.25,
                        border: "1px solid #e5e7eb",
                        borderRadius: 1.5,
                        bgcolor: "#f8fafc",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 11.5,
                        lineHeight: 1.5,
                        color: "#334155",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {JSON.stringify(draft.predicate ?? {}, null, 2)}
                    </Box>
                  </>
                ) : (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={5}
                      value={predicateText}
                      error={Boolean(predicateError)}
                      onChange={(e) => {
                        setPredicateText(e.target.value);
                        try {
                          const parsed = JSON.parse(e.target.value);
                          setPredicateError(null);
                          setDraft((d) => (d ? { ...d, predicate: parsed } : d));
                        } catch (err) {
                          setPredicateError(
                            err instanceof Error ? err.message : "Invalid JSON",
                          );
                        }
                      }}
                      sx={{
                        "& textarea": {
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 12.5,
                          lineHeight: 1.55,
                        },
                      }}
                    />
                    {predicateError && (
                      <Txt sx={{ fontSize: 11.5, color: "#dc2626", mt: 0.5 }}>
                        {predicateError}
                      </Txt>
                    )}
                  </>
                )}
              </Box>

              <TextField
                size="small"
                fullWidth
                label="Commit message (optional)"
                placeholder="Why are you saving this change?"
                value={commitMsg}
                onChange={(e) => setCommitMsg(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <Flx ac g={1}>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={onSave}
                  disabled={busy || Boolean(predicateError)}
                  sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}
                >
                  {busy ? "Saving…" : "Save Rule"}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onDelete}
                  disabled={busy}
                  sx={{ textTransform: "none" }}
                >
                  Delete
                </Button>
                <Button
                  variant="text"
                  onClick={() => setSelectedIdx(null)}
                  disabled={busy}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Flx>
            </Flx>
          </Box>
        )}
      </Box>

      <ChangeHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        entityType={RULE_TYPE_TO_ENTITY[type] || "hard_exception_rule"}
        entityId={`${type}/${program}`}
      />
    </Box>
  );
}
